import { isFunction } from './type-check';

const RETRY_STATE_NOT_START = 0;
const RETRY_STATE_STARTED = 1;
const RETRY_STATE_STOPPED = 2;

/**
 * 给异步函数封装重试逻辑
 * @param {Object} options
 * @param {Object} options.retryFunction 需要封装的异步函数
 * @param {Object} options.settings
 * @param {Number} [options.settings.retries = 5] 重试次数
 * @param {Number} [options.settings.timeout = 1000] 重试间隔
 * @param {onErrorCallback} options.onError
 * @param {onRetryingCallback} [options.onRetrying]
 * @param {Object} options.context 上下文，可选
 * @return {Function} 封装后的函数
 * @example
 * const getUserMedia = promiseRetry({
 *   retryFunction: getUserMedia_,
 *   settings: { retries: 5, timeout: 2000 },
 *   onError: (error, retry, reject) => {
 *     if (error.name === 'NotReadableError') {
 *       retry();
 *     } else {
 *       reject(error);
 *     }
 *   },
 *   onRetrying: retryCount => {
 *     loggerManager.warn(`getUserMedia NotReadableError observed, retrying [${retryCount}/5]`);
 *   }
 * });
 */
interface retryArguments<T> {
  retryFunction: (...args: any[]) => Promise<T>,
  settings: { retries: number | (() => number), timeout: ((retryCount: number) => number) | number; },
  onError?: (params: { error: any, retry: () => void, reject: (reason?: any) => void, retryFuncArgs: any[], retriedCount: number }) => void,
  onRetrying?: (retryCount: number, stopRetry?: () => void) => void,
  onRetryFailed?: (err: Error) => void,
  onRetrySuccess?: (retryCount: number) => void,
  context?: any;
}
function promiseRetry<T>({ retryFunction, settings, onError, onRetrying, onRetryFailed, onRetrySuccess, context }: retryArguments<T>): (...arg: any) => Promise<T> {
  return function (this: any, ...args) {
    const { retries = 5, timeout = 1000 } = settings;
    let retryCount = 0;
    let timer = -1;
    let retryState = RETRY_STATE_NOT_START;
    const run = async (resolve: (arg: any) => void, reject: (arg: any) => void) => {
      const ctx = context || this;
      try {
        const result = await retryFunction.apply(ctx, args);
        if (retryCount > 0 && onRetrySuccess) {
          onRetrySuccess.call(this, retryCount);
        }
        // 执行成功，正常返回
        retryCount = 0;
        resolve(result);
      } catch (error: any) {
        // 用于停止重试
        const stopRetry = () => {
          clearTimeout(timer);
          retryCount = 0;
          retryState = RETRY_STATE_STOPPED;
          reject(error);
        };
        const retry = () => {
          if (retryState !== RETRY_STATE_STOPPED && retryCount < (isFunction(retries) ? retries() : retries)) {
            retryCount++;
            retryState = RETRY_STATE_STARTED;
            if (isFunction(onRetrying)) {
              // @ts-ignore
              onRetrying.call(this, retryCount, stopRetry);
            }
            timer = window.setTimeout(
              () => {
                timer = -1;
                run(resolve, reject);
              },
              isFunction(timeout) ? timeout(retryCount) : timeout
            );
          } else {
            stopRetry();
            if (isFunction(onRetryFailed)) {
              onRetryFailed.call(this, error);
            }
          }
        };
        if (isFunction(onError)) {
          onError.call(this, { error, retry, reject, retryFuncArgs: args, retriedCount: retryCount });
        } else {
          retry();
        }
      }
    };

    return new Promise(run);
  };
}

/**
 * @callback onErrorCallback
 * @param {Error} error 错误信息，可通过 error 来判断是否需要进行重试
 * @param {Function} retry 调用该回调执行重试
 * @param {Function} reject 当不需要重试的时候，reject(error)
 * @return undefined
 * @example
 * function onError(error, retry, reject) {
 *    if (error.name === 'NotReadableError') {
 *      retry();
 *    } else {
 *      reject(error)
 *    }
 * }
 */

/**
 * @callback onRetryingCallback
 * @param {Number} retryCount 当前重试次数
 * @param {Function} stopRetry 可调用该回调停止重试
 * @return undefined
 */

export default promiseRetry;
